import { useIntl } from 'react-intl';
import { useMutation } from '@tanstack/react-query';

// common
import useGeneralHook from 'common/hook/useGeneralHook';
import { handleException } from 'modules/account/detail/helpers/utils';
import useService from 'common/hook/useService';
import { AxiosError } from 'axios';

export interface Password {
  currentPassword: string;
  newPassword: string;
  newPasswordRetype: string;
}

export const defaultPasswordFields: Password = {
  currentPassword: '',
  newPassword: '',
  newPasswordRetype: '',
};

interface MutatingProps {
  onSetDefaultPassword: () => void;
  oldPassword: string;
  newPassword: string;
}

const useUpdateLoggedInUserPassword = () => {
  const { formatMessage } = useIntl();

  const { enqueueSnackbar } = useGeneralHook();
  const service = useService();

  return useMutation({
    mutationFn: async (props: MutatingProps) => {
      try {
        if (!service) return null;

        const { oldPassword, newPassword, onSetDefaultPassword } = props;

        const result = await service.UserController_changePassword({
          oldPassword,
          newPassword,
        });

        if (result.success) {
          enqueueSnackbar({
            message: formatMessage({ id: 'updatePasswordSuccess' }),
            variant: 'success',
          });
          onSetDefaultPassword();
        }
      } catch (error) {
        if (error instanceof AxiosError && error.response?.data.code === 'WRONG_OLD_PASSWORD') {
          enqueueSnackbar({
            message: formatMessage({ id: 'account.error.incorrectPassword' }),
            variant: 'error',
          });
        }

        handleException(error, { notifyUser: false });
        throw error;
      }
    },
  }).mutateAsync;
};

export default useUpdateLoggedInUserPassword;
